import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getTable } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, canAccess, PLAN_LABELS } from '../../utils/helpers';
import { showToast } from '../common/Toast';

const PublicationDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pub, setPub] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getTable('tables/publications', { limit: 200 });
        const all = res.data || [];
        const found = all.find((p) => p.slug === slug);

        if (!found) {
          navigate('/publications');
          return;
        }

        setPub(found);

        const relatedList = all
          .filter((p) => p.category === found.category && p.slug !== found.slug)
          .slice(0, 3);
        setRelated(relatedList);
      } catch (e) {
        console.error('Erreur chargement publication:', e);
        navigate('/publications');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  const handleDownload = (e) => {
    e.preventDefault();
    showToast('Téléchargement simulé : ' + pub.title);
  };

  if (loading) {
    return (
      <section className="section">
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>Chargement...</div>
        </div>
      </section>
    );
  }

  if (!pub) return null;

  const hasAccess = user && canAccess(user.plan, pub.access_level);
  const userPlan = user ? user.plan : null;

  return (
    <>
      <section className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Accueil</Link> / <Link to="/publications">Publications</Link> / {pub.title}
          </div>
          <h1>{pub.title}</h1>
        </div>
      </section>

      <section className="section">
        <div className="container doc-hero">
          <div>
            <img src={pub.image_url} alt={pub.title} loading="lazy" />
            <ul className="doc-meta-list">
              <li><span>Type de document</span><span>{pub.doc_type}</span></li>
              <li><span>Thématique</span><span>{pub.category}</span></li>
              <li><span>Auteur</span><span>{pub.author || '—'}</span></li>
              <li><span>Date de publication</span><span>{formatDate(pub.publish_date)}</span></li>
              <li><span>Zone géographique</span><span>{pub.country_focus || '—'}</span></li>
              <li><span>Pages</span><span>{pub.pages ? pub.pages + ' pages' : '—'}</span></li>
              <li><span>Téléchargements</span><span>{pub.downloads || 0}</span></li>
              <li>
                <span>Niveau d'accès</span>
                <span>
                  <span className={`tag access-${pub.access_level}`}>
                    {PLAN_LABELS[pub.access_level] || pub.access_level}
                  </span>
                </span>
              </li>
            </ul>
          </div>

          <div>
            <span className="section-kicker">{pub.category}</span>
            <p style={{ fontSize: '1.08rem', color: '#212a33', marginBottom: '20px' }}>
              {pub.summary}
            </p>

            {hasAccess ? (
              <div>
                <div className="doc-full-content">
                  <h3><i className="fa-solid fa-circle-check"></i> Contenu complet</h3>
                  <div style={{ color: '#212a33', lineHeight: '1.8' }}>
                    {pub.content ? pub.content.split('\n').map((p, i) => (
                      <p key={i}>{p}</p>
                    )) : <p>Contenu non disponible.</p>}
                  </div>
                  <button className="btn btn-dark" onClick={handleDownload} style={{ marginTop: '20px' }}>
                    <i className="fa-solid fa-download"></i> Télécharger le document
                  </button>
                </div>
              </div>
            ) : (
              <div className="paywall">
                <i className="fa-solid fa-lock lock-icon"></i>
                {!user ? (
                  <>
                    <h3>Connectez-vous pour accéder à ce contenu</h3>
                    <p>
                      Ce document est classé "{PLAN_LABELS[pub.access_level]}".
                      Créez un compte gratuit ou connectez-vous, puis souscrivez à la formule adaptée
                      pour le consulter en intégralité.
                    </p>
                    <div className="paywall-actions">
                      <Link to="/inscription" className="btn btn-primary">Créer un compte gratuit</Link>
                      <Link to="/connexion" className="btn btn-outline">Se connecter</Link>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>Mettez à niveau votre abonnement</h3>
                    <p>
                      Ce document nécessite la formule "{PLAN_LABELS[pub.access_level]}" ou supérieure.
                      Votre formule actuelle est "{PLAN_LABELS[userPlan]}".
                    </p>
                    <div className="paywall-actions">
                      <Link to="/abonnements" className="btn btn-primary">Voir les formules d'abonnement</Link>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section bg-alt">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">Publications similaires</h2>
            </div>
            <div className="grid grid-3">
              {related.map((p) => (
                <Link key={p.id} to={`/publication/${p.slug}`} className="card">
                  <div className="card-media">
                    <img src={p.image_url} alt={p.title} loading="lazy" />
                  </div>
                  <div className="card-body">
                    <div className="card-tags">
                      <span className="tag">{p.doc_type}</span>
                      <span className={`tag access-${p.access_level}`}>{p.access_level}</span>
                    </div>
                    <h3>{p.title}</h3>
                    <div className="card-meta">
                      <span>{p.author || ''}</span>
                      <span>{formatDate(p.publish_date)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default PublicationDetail;